import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";

const bestMatch = (list, ql) => {
  if (!ql) return 0;
  const lbl = (o) => String(o.label || "").toLowerCase();
  let exact = -1, starts = -1;
  list.forEach((o, i) => {
    const l = lbl(o);
    if (exact < 0 && l === ql) exact = i;
    if (starts < 0 && l.startsWith(ql)) starts = i;
  });
  return exact >= 0 ? exact : starts >= 0 ? starts : 0;
};

export default function SearchSelect({ options, value, onChange, placeholder = "Search…", testid, className = "", autoFocus = false, disabled = false, nextFocus }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const wrap = useRef(null);
  const listRef = useRef(null);
  const [pos, setPos] = useState(null);
  const selected = options.find(o => o.value === value);
  const inputRef = useRef(null);

  const place = () => {
    const r = inputRef.current?.getBoundingClientRect(); if (!r) return;
    const below = window.innerHeight - r.bottom;
    setPos({ left: r.left, minWidth: r.width, ...(below < 220 && r.top > below ? { bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(256, r.top - 12) } : { top: r.bottom + 4, maxHeight: Math.min(256, below - 12) }) });
  };
  useEffect(() => {
    if (!open) return;
    place();
    const h = () => place();
    window.addEventListener("scroll", h, true); window.addEventListener("resize", h);
    return () => { window.removeEventListener("scroll", h, true); window.removeEventListener("resize", h); };
  }, [open]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return ql ? options.filter(o => [o.label, o.sub, ...(o.keywords || [])].some(v => String(v || "").toLowerCase().includes(ql))) : options;
  }, [options, q]);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) { setOpen(false); setQ(""); } };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  useEffect(() => { setHi(bestMatch(filtered, q.trim().toLowerCase())); }, [q, open, filtered]);
  useEffect(() => { listRef.current?.children[hi]?.scrollIntoView?.({ block: "nearest" }); }, [hi]);

  const focusNext = () => {
    if (!nextFocus) return;
    setTimeout(() => { const el = document.querySelector(`[data-testid='${nextFocus}']`); el?.focus(); el?.select?.(); }, 30);
  };
  const pick = (o, viaKey = false) => { onChange(o.value, o); setOpen(false); setQ(""); if (viaKey) focusNext(); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) { if (open && filtered[hi]) { e.preventDefault(); pick(filtered[hi], true); } }
    else if (e.key === "Escape") { setOpen(false); setQ(""); }
  };

  return (
    <div ref={wrap} className={`relative ${className}`} data-testid={testid ? `${testid}-wrap` : undefined}>
      <div className="relative">
        <input ref={inputRef} data-testid={testid} disabled={disabled} autoFocus={autoFocus} role="combobox" aria-expanded={open}
          className="cell-input pr-7 truncate" value={open ? q : (selected?.label || "")} placeholder={selected ? selected.label : placeholder}
          onFocus={() => setOpen(true)} onClick={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onKeyDown={onKey} />
        {selected && !disabled
          ? <button type="button" tabIndex={-1} data-testid={testid ? `${testid}-clear` : undefined} onMouseDown={(e) => { e.preventDefault(); onChange("", null); setQ(""); }} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-0.5"><X size={12} /></button>
          : <ChevronDown size={13} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />}
      </div>
      {open && !disabled && pos && (
        <div ref={listRef} data-testid={testid ? `${testid}-list` : undefined} style={pos} className="fixed z-[60] w-max max-w-[min(90vw,420px)] overflow-y-auto bg-white border border-slate-300 rounded-md shadow-lg py-1">
          {filtered.map((o, i) => (
            <div key={o.value} data-testid={testid ? `${testid}-opt-${o.value}` : undefined} aria-selected={i === hi} onMouseDown={(e) => { e.preventDefault(); pick(o); }} onMouseEnter={() => setHi(i)}
              className={`px-3 py-1.5 cursor-pointer flex items-center justify-between gap-4 text-sm transition-colors ${i === hi ? "bg-[#2563EB] text-white" : "text-slate-900"} ${o.value === value ? "font-semibold" : ""}`}>
              <span className="truncate">{o.label}</span>
              {o.sub && <span className={`text-[11px] mono shrink-0 ${i === hi ? "text-white/90" : (o.subCls || "text-slate-500")}`}>{o.sub}</span>}
            </div>
          ))}
          {!filtered.length && <div className="px-3 py-2 text-xs text-slate-400">No matches for “{q}”</div>}
        </div>
      )}
    </div>
  );
}
